import {
  Banner,
  Box,
  Button,
  DatePicker,
  Icon,
  LegacyCard,
  Popover,
  TextField,
  VerticalStack,
} from "@shopify/polaris";
import { CalendarMinor } from "@shopify/polaris-icons";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ROUTES_NAME } from "../../../config/constants";
import { ACCESS_TOKEN } from "../../../constants";
import { authenticatedFetch } from "../../../util/authenticatedFetch";

const readResponsePayload = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_error) {
    return text;
  }
};

const errorMessage = (payload, response, fallback) => {
  const serverMessage =
    (typeof payload === "string" && payload.trim() && payload) ||
    [payload?.message, payload?.error, payload?.detail].find(
      (value) => typeof value === "string" && value.trim()
    );
  return serverMessage || `${fallback} (${response.status})`;
};

const requireEligibleOrderCount = (payload) => {
  const count = payload?.count;
  if (
    typeof count !== "number" ||
    !Number.isSafeInteger(count) ||
    count < 0
  ) {
    throw new Error("The eligible-order check returned an invalid count.");
  }
  return count;
};

const requestHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN)}`,
  "Content-Type": "application/json",
});

const emptyPreview = (shopId) => ({
  shopId,
  requestId: null,
  loading: false,
  count: null,
  date: null,
  error: null,
});

const SupportBackInTime = () => {
  const { id } = useParams();
  const currentShopId = String(id ?? "");
  const routeShopId = useRef(currentShopId);
  routeShopId.current = currentShopId;
  const requestSequence = useRef(0);
  const previewController = useRef(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [{ month, year }, setVisibleMonth] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const [preview, setPreview] = useState(emptyPreview(currentShopId));

  const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
  const previewForCurrentStore = preview.shopId === currentShopId ? preview : null;

  useEffect(() => {
    previewController.current?.abort();
    requestSequence.current += 1;
    setPreview(emptyPreview(currentShopId));
    return () => previewController.current?.abort();
  }, [currentShopId]);

  const clearPreview = () => {
    previewController.current?.abort();
    requestSequence.current += 1;
    setPreview(emptyPreview(currentShopId));
  };

  const checkEligibleOrders = async () => {
    const target = {
      shopId: currentShopId,
      date: formattedDate,
      requestId: ++requestSequence.current,
    };
    previewController.current?.abort();
    const controller = new AbortController();
    previewController.current = controller;
    setPreview({ ...target, loading: true, count: null, error: null });

    try {
      const response = await authenticatedFetch(
        `${process.env.REACT_APP_BACKEND_API_URL}${ROUTES_NAME.possible_orders(target.shopId)}`,
        {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
          headers: requestHeaders(),
          body: JSON.stringify({ backInTimeType: "USER", date: target.date }),
        }
      );
      const payload = await readResponsePayload(response);
      if (!response.ok) {
        throw new Error(errorMessage(payload, response, "Eligible-order check failed"));
      }
      const count = requireEligibleOrderCount(payload);
      if (
        routeShopId.current !== target.shopId ||
        requestSequence.current !== target.requestId
      ) {
        return;
      }
      setPreview({ ...target, loading: false, count, error: null });
    } catch (error) {
      if (error?.name === "AbortError") return;
      if (
        routeShopId.current !== target.shopId ||
        requestSequence.current !== target.requestId
      ) {
        return;
      }
      setPreview({
        ...target,
        loading: false,
        count: null,
        error: error?.message || "Eligible-order check failed",
      });
    }
  };

  return (
    <>
      <Banner title="Historical support preview is read-only" status="warning">
        <p>
          PalSync has no enabled SUPPORT importer. This tool never spends a store
          token or queues work; use it only to inspect the eligible order count.
        </p>
      </Banner>
      {previewForCurrentStore?.error ? (
        <Banner title="Eligible-order check failed" status="critical">
          <p>{previewForCurrentStore.error}</p>
        </Banner>
      ) : null}
      {previewForCurrentStore?.count === 0 ? (
        <Banner title="No eligible historical orders" status="info">
          <p>
            Store {currentShopId} has no eligible orders since {previewForCurrentStore.date}.
            No token was consumed and no work was queued.
          </p>
        </Banner>
      ) : null}
      {Number.isSafeInteger(previewForCurrentStore?.count) &&
      previewForCurrentStore.count > 0 ? (
        <Banner title="Eligible historical orders" status="info">
          <p>
            Store {currentShopId} has {previewForCurrentStore.count} eligible order(s)
            since {previewForCurrentStore.date}. This is a read-only result; no token
            was consumed and no work was queued.
          </p>
        </Banner>
      ) : null}

      <VerticalStack inlineAlign="start" gap="4">
        <p>Choose a start date to preview historical Shopify order volume safely.</p>
        <Box minWidth="276px" padding={{ xs: 2 }}>
          <div className="d-flex gap-20 flex-wrap">
            <Popover
              active={popoverOpen}
              autofocusTarget="none"
              preferredAlignment="left"
              fullWidth
              preferredPosition="below"
              onClose={() => setPopoverOpen(false)}
              activator={
                <TextField
                  label="Orders created since"
                  prefix={<Icon source={CalendarMinor} />}
                  value={formattedDate}
                  onFocus={() => setPopoverOpen(true)}
                  onChange={() => {}}
                  autoComplete="off"
                />
              }
            >
              <LegacyCard>
                <DatePicker
                  month={month}
                  year={year}
                  selected={selectedDate}
                  disableDatesAfter={new Date()}
                  onMonthChange={(nextMonth, nextYear) =>
                    setVisibleMonth({ month: nextMonth, year: nextYear })
                  }
                  onChange={({ end }) => {
                    clearPreview();
                    setSelectedDate(end);
                    setPopoverOpen(false);
                  }}
                />
              </LegacyCard>
            </Popover>
            <Button
              primary
              onClick={checkEligibleOrders}
              loading={previewForCurrentStore?.loading}
              disabled={!currentShopId}
            >
              Check eligible orders
            </Button>
          </div>
        </Box>
      </VerticalStack>
    </>
  );
};

export default SupportBackInTime;
